import { Box, Heading, Link } from "@chakra-ui/react";
import LandingContent from "../../../components/landing-content";
import { StdLink } from "../../../components/StdLink";
import { H3, LB } from "../../../components/text-formatting";
import { siteConfig } from "../../../configuration/config";
import { contactEmail } from "../terms";

export default function Gdpr() {
  return (
    <LandingContent>
      <Heading mb={3} fontSize={"xl"}>
        {siteConfig.serviceName} Privacy Policy
      </Heading>
      <div>
        Last Updated: November 26, 2024
        <LB />
        At {siteConfig.serviceName}, we take your privacy seriously and are committed to protecting
        your personal data in accordance with the General Data Protection Regulation (GDPR). This
        Privacy Policy outlines how we collect, use, and store your personal data when using{" "}
        {siteConfig.serviceName}, a language learning application.
        <H3 text="Collection of Personal Data" />
        When using {siteConfig.serviceName}, we collect the following personal data:
        <LB />
        a. User conversations: We save your conversations with our artificial tutors to enhance your
        learning experience and to allow you to review past conversations.
        <br />
        b. User settings: We save your settings in the Application to provide a personalized
        experience.
        <br />
        c. Cookies: We use cookies for authentication and analytics purposes. Authentication cookies
        are used to identify you when you log in, while non-identifiable analytics cookies are used
        to analyze and optimize the
        {"Application's"} performance.
        <br />
        d. Voice data: Your voice data is sent to third parties for processing to enable the
        voice-based features of {siteConfig.serviceName} (see sub-processor list below).
        <br />
        e. Account email and password: We save your email address and password as part of the sign
        up process. This data is saved with identity provide{" "}
        <StdLink
          href="https://auth0.com/docs/secure/data-privacy-and-compliance"
          text="Auth0"
          target="_blank"
          rel="nofollow noreferrer"
        />{" "}
        in the European Union.
        <br />
        f: Payment data: if you signed up via our payment gateway, Stripe, then your payment data is
        handled and stored by{" "}
        <StdLink
          href="https://stripe.com/en-dk/legal/ssa"
          text="Stripe Payments Europe"
          rel="nofollow noreferrer"
          target="_blank"
        />
        <H3 text="Data Storage and Processing" />
        Artificial Intelligence (AI) models are used to process your voice and text input. We use
        the following model providers for tasks such as response generation, language understanding,
        language generation, and voice recognition:{" "}
        <Link
          href="https://openai.com/policies/privacy-policy"
          target="_blank"
          rel="noreferrer"
          color={"brand.secondary.main"}
        >
          OpenAI
        </Link>
        ,{" "}
        <Link
          href="https://www.anthropic.com/legal/privacy"
          target="_blank"
          rel="noreferrer"
          color={"brand.secondary.main"}
        >
          Anthropic
        </Link>{" "}
        and
        <Link
          href="https://wow.groq.com/privacy-policy/"
          target="_blank"
          rel="noreferrer"
          color={"brand.secondary.main"}
        >
          Groq
        </Link>
        . Please refer to their privacy policies for more information.
        <LB />
        For European users, your personal data, including conversations and voice data, is stored
        within the European Union at data centers operated by Microsoft, Stripe Payments Europe and
        Auth0. Furthermore, conversations are shared with the AI providers mentioned above. For
        non-European users, your voice data is processed on servers close to your location to ensure
        low latency, while other data is stored in the European Union.
        <LB />
        When using {siteConfig.serviceName} you consent to the collection and processing of your
        voice transcriptions and text input, and that we sent your data to Microsoft and the AI
        providers mentioned above for processing.
        <LB />
        When using {siteConfig.serviceName} you also consent to abstain from sharing any sensitive
        or personal information with our artificial language tutors.
        <LB />
        <H3 text="Sub-processors" />
        We use the following sub-processors to store and process your personal data in accordance
        with the GDPR:
        <Box mt="3" ml="15px">
          <ul>
            <li>
              Microsoft Azure North Europe, Takeda Ireland Ltd (Grange Castle) New Nangor Road,
              Grange Dublin 22, Co. Dublin Ireland.
            </li>
            <li>
              Microsoft Azure, West Europe, Evert van de Beekstraat 354, 1118 CZ Luchthaven
              Schiphol, Noord-Holland, Netherlands.
            </li>
            <li>
              OpenAI Ireland Ltd. 1st Floor, The Liffey Trust Centre, 117-126 Sheriff Street Upper,
              Dublin 1, D01 YC43, Ireland.
            </li>
            <li>
              Anthropic Ireland Limited, 6th Floor South Bank House, Barrow Street, Dublin 4,
              Ireland.
            </li>
            <li>Groq, Inc. 301 Castro St., Suite 200 Mountain View, CA 94041, USA.</li>
            <li>Auth0, Frankfurt, Germany, Authentication.</li>
            <li>Sentry, Frankfurt, Germany, Error logging.</li>
            <li>Mixpanel, Eemshaven, Netherlands, Usage tracking.</li>
          </ul>
        </Box>
        <H3 text="Right to Be Forgotten" />
        You have the right to request the deletion of your personal data from our systems. To
        exercise this right, please send an email to {contactEmail} with the subject{" "}
        {'"Right to Be Forgotten."'} Please settle any unpaid bills before submitting the request to
        ensure a seamless process. Upon receiving your request, we will delete your personal data
        from our systems within 30 days, as required by the GDPR.
        <H3 text="Data Protection" />
        We implement appropriate technical and organizational measures to ensure the security of
        your personal data and protect it against unauthorized access, disclosure, alteration, or
        destruction.
        <H3 text="Changes to GDPR Information" />
        We reserve the right, at our sole discretion, to modify or update this Privacy Policy at any
        time. By continuing to access or use {siteConfig.serviceName} after such modifications, you
        agree to be bound by the updated Privacy Policy
        <H3 text="Contact Information" />
        If you have any questions about this Privacy Policy or our data protection practices, please
        contact us at {contactEmail}.
        <LB />
        By using {siteConfig.serviceName}, you acknowledge that you have read, understood, and agree
        to the terms outlined in this Privacy Policy.
      </div>

      <LB />
    </LandingContent>
  );
}
